// flyingScytheEffect.ts
import { Particle } from '../ParticleLayer'

const CONFIG = {
    colorCyan: '#22d3ee',
    colorCyanDark: '#0891b2',
    colorGold: '#fbbf24',
    colorGoldDark: '#b45309',
    colorFeather: '#1e1b4b',
    colorDarkTear: '#020617', // almost black for the spectral tear
    spritePath: '/assets/effects/flying_scythe/scythe.svg',
    scale: 1.8 // Increased 3x from 0.6 -> 1.8 (Massive)
}

const spriteCache: Record<string, HTMLImageElement> = {}
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image()
        img.src = src
        spriteCache[src] = img
    }
    return spriteCache[src]
}
getSprite(CONFIG.spritePath) // Preload

export type FlyingScytheData = {
    state: 'windup' | 'strike' | 'fade'
    timeInState: number
    hasImpacted?: boolean
    hideScythe?: boolean

    currX: number
    currY: number
    currAngle: number

    baseAngle: number

    trail: { x: number; y: number; angle: number; age: number }[]
    feathers: { x: number; y: number; vx: number; vy: number; rot: number; vrot: number; life: number; scale: number; color: string }[]
    sparks: { x: number; y: number; vx: number; vy: number; life: number; color: string; size: number }[]
    slashes: { x: number; y: number; angle: number; life: number; scale: number; length: number }[] // The massive spectral tear
}

export type FlyingScytheEffect = {
    kind: 'flying_scythe'
    t: number
    ox: number // Owner X
    oy: number // Owner Y
    tx: number // Target X
    ty: number // Target Y
    isOpponent: boolean
    data: FlyingScytheData
}

export function createFlyingScytheEffect(ox: number, oy: number, tx: number, ty: number, isOpponent: boolean = false): FlyingScytheEffect {
    const dx = tx - ox;
    const dy = ty - oy;
    let baseAngle = Math.atan2(dy, dx);
    if (baseAngle < 0) baseAngle += Math.PI * 2;

    return {
        kind: 'flying_scythe',
        t: 0,
        ox, oy, tx, ty,
        isOpponent,
        data: {
            state: 'windup',
            timeInState: 0,
            currX: ox,
            currY: oy,
            currAngle: baseAngle,
            baseAngle,
            hasImpacted: false,
            hideScythe: false,
            trail: [],
            feathers: [],
            sparks: [],
            slashes: []
        }
    }
}

function drawFeather(ctx: CanvasRenderingContext2D, size: number, color: string) {
    ctx.beginPath();
    ctx.moveTo(0, -size);
    ctx.quadraticCurveTo(size * 0.3, 0, 0, size);
    ctx.quadraticCurveTo(-size * 0.3, 0, 0, -size);
    ctx.closePath();
    ctx.moveTo(0, -size);
    ctx.lineTo(0, size);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 0.5;
    ctx.stroke();
}

function drawSpectralSlash(ctx: CanvasRenderingContext2D, s: FlyingScytheData['slashes'][0]) {
    ctx.save();
    ctx.translate(s.x, s.y);
    ctx.rotate(s.angle);
    ctx.scale(s.scale, s.scale);

    const alpha = Math.min(1, s.life * 2);
    const w = s.length;
    const h = 20 * s.life; // Slash narrows as it fades

    ctx.globalAlpha = alpha;

    // Dark inner tear
    ctx.shadowBlur = 40;
    ctx.shadowColor = CONFIG.colorCyanDark;

    ctx.beginPath();
    ctx.ellipse(0, 0, w, h, 0, 0, Math.PI * 2);
    ctx.fillStyle = CONFIG.colorDarkTear;
    ctx.fill();

    // Cyan glowing edge
    ctx.globalCompositeOperation = 'lighter';
    ctx.beginPath();
    ctx.ellipse(0, 0, w, h * 0.4, 0, 0, Math.PI * 2);
    ctx.fillStyle = CONFIG.colorCyan;
    ctx.fill();

    // Pure white hot core
    ctx.beginPath();
    ctx.ellipse(0, 0, w * 0.8, h * 0.1, 0, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    ctx.restore();
}

export function renderFlyingScytheEffect(effect: FlyingScytheEffect, ctx: CanvasRenderingContext2D): boolean {
    const d = effect.data;
    d.timeInState += 16.7;

    let globalAlpha = 1.0;

    // Center of screen / battle
    const centerX = (effect.ox + effect.tx) / 2;
    const centerY = (effect.oy + effect.ty) / 2;

    // Windup goes to strictly the center of the screen, + 10% towards the opponent
    const windupTargetX = centerX + (effect.tx - centerX) * 0.2; // 0.2 of half-distance = 10% of full
    const windupTargetY = centerY + (effect.ty - centerY) * 0.2;

    let startStrike = { x: windupTargetX + 200, y: windupTargetY - 150 }; // Right-side windup peak
    // 3. Из замаха удар идет справа налево и прошибает оппонента насквозь в левую сторону:
    let endStrike = { x: effect.tx - 1500, y: effect.ty + 200 };

    if (effect.isOpponent) {
        startStrike.y = effect.ty - 90;
        endStrike.y = effect.ty - 200;
    }

    // ----- State Machine & Physics -----
    if (d.state === 'windup') {
        const WINDUP_TIME = 2400; // 3x slower windup
        const prog = Math.min(1, d.timeInState / WINDUP_TIME);

        // easeOutCubic
        const e = 1 - Math.pow(1 - prog, 3);

        const p0 = { x: effect.ox, y: effect.oy };
        const p2 = startStrike;
        const p1 = { x: (p0.x + p2.x) / 2, y: Math.min(p0.y, p2.y) - 200 };

        d.currX = (1 - e) * (1 - e) * p0.x + 2 * (1 - e) * e * p1.x + e * e * p2.x;
        d.currY = (1 - e) * (1 - e) * p0.y + 2 * (1 - e) * e * p1.y + e * e * p2.y;

        const startRot = 0;
        const targetRot = Math.PI / 2.5;
        d.currAngle = startRot + (targetRot - startRot) * e;

        if (Math.random() > 0.6) {
            d.sparks.push({
                x: d.currX + (Math.random() - 0.5) * 180,
                y: d.currY + (Math.random() - 0.5) * 180,
                vx: 0, vy: -1.5, life: 0.8, color: CONFIG.colorCyan, size: 3
            });
        }

        if (d.timeInState >= WINDUP_TIME) {
            d.state = 'strike';
            d.timeInState = 0;
        }

    } else if (d.state === 'strike') {
        const STRIKE_TIME = 800;
        const prog = Math.min(1, d.timeInState / STRIKE_TIME);

        // Linear interpolation so it doesn't linger and spawn stationary copies
        const e = prog;

        d.currX = startStrike.x + (endStrike.x - startStrike.x) * e;
        d.currY = startStrike.y + (endStrike.y - startStrike.y) * e;

        const startRot = Math.PI / 2.5;
        const endRot = -Math.PI * 1.5;
        d.currAngle = startRot + (endRot - startRot) * e;

        if (prog > 0.05 && prog < 0.95) {
            d.trail.push({ x: d.currX, y: d.currY, angle: d.currAngle, age: 1.0 });
        }

        if (!d.hasImpacted && d.currX <= effect.tx + 100) {
            d.hasImpacted = true;

            // Massive narrative SLASH effect cutting space itself
            const slashAngle = Math.atan2((endStrike.y - startStrike.y), (endStrike.x - startStrike.x));
            d.slashes.push({
                x: effect.tx, y: effect.ty,
                angle: slashAngle,
                life: 1.0, scale: 1.0, length: 230
            });

            for (let i = 0; i < 40; i++) {
                const angle = Math.random() * Math.PI * 2;
                const spd = 6 + Math.random() * 15;
                d.sparks.push({
                    x: effect.tx, y: effect.ty,
                    vx: Math.cos(angle) * spd,
                    vy: Math.sin(angle) * spd,
                    life: 1.0,
                    color: Math.random() > 0.3 ? CONFIG.colorCyanDark : CONFIG.colorGoldDark,
                    size: 4 + Math.random() * 6
                });
            }
            for (let i = 0; i < 30; i++) {
                const angle = Math.random() * Math.PI * 2;
                const spd = 4 + Math.random() * 12;
                d.feathers.push({
                    x: effect.tx + (Math.random() - 0.5) * 80,
                    y: effect.ty + (Math.random() - 0.5) * 80,
                    vx: Math.cos(angle) * spd,
                    vy: Math.sin(angle) * spd > 0 ? -Math.sin(angle) * spd : Math.sin(angle) * spd - 4,
                    rot: Math.random() * Math.PI * 2,
                    vrot: (Math.random() - 0.5) * 0.4,
                    life: 1.0,
                    scale: 9 + Math.random() * 14,
                    color: Math.random() > 0.6 ? '#020617' : CONFIG.colorFeather
                });
            }
        }

        if (d.timeInState >= STRIKE_TIME) {
            d.state = 'fade';
            d.timeInState = 0;
            d.hideScythe = true;
        }

    } else if (d.state === 'fade') {
        const FADE_TIME = 1500; // Allow feathers and slashes to linger and fade
        const prog = Math.min(1, d.timeInState / FADE_TIME);
        globalAlpha = 1 - prog;

        if (prog >= 1 && d.sparks.length === 0 && d.feathers.length === 0 && d.slashes.length === 0) {
            return false;
        }
    }

    // --- Particle Updates ---
    d.trail.forEach(t => t.age -= 0.05); // Slower trail fade since it's 3x slower execution
    d.trail = d.trail.filter(t => t.age > 0);

    for (let i = d.slashes.length - 1; i >= 0; i--) {
        const s = d.slashes[i];
        s.life -= 0.02; // Very slow majestic fade
        s.scale += 0.015; // Expands outward
        if (s.life <= 0) {
            d.slashes[i] = d.slashes[d.slashes.length - 1];
            d.slashes.pop();
        }
    }

    for (let i = d.sparks.length - 1; i >= 0; i--) {
        const p = d.sparks[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.02;
        p.vx *= 0.94;
        p.vy *= 0.94;
        if (p.life <= 0) {
            d.sparks[i] = d.sparks[d.sparks.length - 1];
            d.sparks.pop();
        }
    }

    for (let i = d.feathers.length - 1; i >= 0; i--) {
        const p = d.feathers[i];
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vrot;
        p.life -= 0.008; // Feather float
        p.vx *= 0.96;
        p.vy += 0.15; // slow gravity
        if (p.life <= 0) {
            d.feathers[i] = d.feathers[d.feathers.length - 1];
            d.feathers.pop();
        }
    }

    // ----- RENDERING -----
    ctx.save();
    ctx.globalAlpha = globalAlpha;

    // 1. Draw Slashes!
    d.slashes.forEach(s => {
        drawSpectralSlash(ctx, s);
    });

    // 2. Draw Sparks
    ctx.globalCompositeOperation = 'lighter';
    d.sparks.forEach(p => {
        ctx.globalAlpha = p.life * globalAlpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
    });

    ctx.globalAlpha = globalAlpha;
    ctx.globalCompositeOperation = 'source-over';

    const img = getSprite(CONFIG.spritePath);
    const renderSprite = (x: number, y: number, angle: number, alpha: number) => {
        if (!img.complete || img.naturalWidth <= 0 || d.hideScythe) return;

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.scale(CONFIG.scale, effect.isOpponent ? -CONFIG.scale : CONFIG.scale);
        ctx.globalAlpha = alpha * globalAlpha;

        const w = img.naturalWidth;
        const h = img.naturalHeight;

        const cxOffsetX = w * 0.573;
        const cyOffsetY = h * 0.186;

        ctx.drawImage(img, -cxOffsetX, -cyOffsetY, w, h);
        ctx.restore();
    }

    // 3. Draw Afterimage Trail
    if (d.trail.length > 0 && !d.hideScythe) {
        ctx.globalCompositeOperation = 'lighter';
        d.trail.forEach((t, i) => {
            const alpha = t.age * 0.2;
            if (img.complete && img.naturalWidth > 0) {
                ctx.save();
                ctx.translate(t.x, t.y);
                ctx.rotate(t.angle);
                ctx.scale(CONFIG.scale, effect.isOpponent ? -CONFIG.scale : CONFIG.scale);

                ctx.globalAlpha = alpha * globalAlpha;
                ctx.shadowColor = CONFIG.colorCyanDark;
                ctx.shadowBlur = 30;

                const w = img.naturalWidth;
                const h = img.naturalHeight;
                const cxOffsetX = w * 0.573;
                const cyOffsetY = h * 0.186;

                ctx.drawImage(img, -cxOffsetX, -cyOffsetY, w, h);
                ctx.restore();
            }
        });
        ctx.globalCompositeOperation = 'source-over';
    }

    // 4. Draw Main Scythe Sprite
    if (globalAlpha > 0 && !d.hideScythe) {
        ctx.shadowColor = 'rgba(0,0,0,0.9)';
        ctx.shadowBlur = 20;
        ctx.shadowOffsetY = 20;

        renderSprite(d.currX, d.currY, d.currAngle, 1.0);

        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
    }

    // 5. Draw Feathers Over everything
    d.feathers.forEach(p => {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.min(1, p.life * 2) * globalAlpha;
        drawFeather(ctx, p.scale, p.color);
        ctx.restore();
    });

    ctx.restore();
    return true;
}
